<script setup lang="ts">
import { storeToRefs } from 'pinia';
import CashbackItem from '@/components/sidebar/menu/CashbackItem.vue';
import { useLoyaltyHubStore } from '@/stores/loyalty-hub';

// "Escolha seu Cashback" (mesmo conteúdo do ?open=cashback / CashbackMenu), mas
// no formato de bottom-sheet slide-up da roletinha, empilhado por cima do hub.
const store = useLoyaltyHubStore();
const { isCashbackOpen } = storeToRefs(store);

// Mesma lista/estilo do CashbackMenu (components/sidebar/menu/CashbackMenu.vue).
const items = [
  {
    name: 'Slots',
    percent: '20%',
    img: '/assets/images/menu-lateral-grid/slots.png',
    url: '/promotions/cashback-diario-ativo',
  },
  {
    name: 'Aviator',
    percent: '20%',
    img: '/assets/images/menu-lateral-grid/aviator.png',
    url: '/promotions/cashback-aviator',
  },
  {
    name: 'Roletas',
    percent: '5%',
    img: '/assets/images/menu-lateral-grid/ao-vivo.png',
    enabled: false,
    url: '/promotions/cashback-roletas',
  },
  {
    name: 'Crash',
    percent: '20%',
    img: '/assets/images/menu-lateral-grid/crash.png',
    enabled: false,
    url: '/promotions/cashback-crash',
  },
];

const blueGradient =
  'radial-gradient(168.3% 138% at 36.76% -16.67%, #1515E5 0%, #000098 100%)';

const orangeGradient =
  'radial-gradient(168.3% 138% at 36.76% -16.67%, #FF704A 0%, #FF3400 100%)';

const visible = ref(false);
const sheetOpen = ref(false);

watch(
  isCashbackOpen,
  async (opened) => {
    if (opened) {
      visible.value = true;
      await nextTick();
      sheetOpen.value = true;
    } else {
      sheetOpen.value = false;
    }
  },
  { immediate: true },
);

function onClosed() {
  visible.value = false;
}

function close() {
  store.closeCashback();
}

function handleSelect(url: string) {
  // Navega pra promoção; o watch de rota do hub fecha o hub, e aqui fechamos
  // o sheet.
  close();
  navigateTo(localeLink(url));
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="lhc-overlay"
      @dragstart.prevent
    >
      <div
        class="lhc-backdrop"
        :class="{ 'lhc-backdrop--out': !sheetOpen }"
        @click="close"
      />

      <Transition
        name="lhc-slide-up"
        @after-leave="onClosed"
      >
        <div
          v-if="sheetOpen"
          class="lhc-screen"
        >
          <header class="lhc-topbar">
            <p class="lhc-title">Escolha seu Cashback</p>
            <button
              aria-label="Fechar"
              class="lhc-close"
              type="button"
              @click="close"
            >
              <NuxtIcon name="close" />
            </button>
          </header>

          <div class="lhc-list">
            <CashbackItem
              v-for="item in items"
              :key="item.name"
              :blue-gradient="blueGradient"
              :item="item"
              :orange-gradient="orangeGradient"
              @select="handleSelect"
            />
          </div>

          <button
            class="lhc-fechar"
            type="button"
            @click="close"
          >
            FECHAR
          </button>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<style>
.lhc-slide-up-enter-active {
  animation: lhc-slide-up 0.32s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  will-change: transform, opacity;
}
.lhc-slide-up-leave-active {
  animation: lhc-slide-up 0.24s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
  will-change: transform, opacity;
}
@keyframes lhc-slide-up {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

<style scoped lang="scss">
.lhc-overlay {
  @apply fixed inset-0 z-[9995] flex flex-col justify-end select-none;

  img {
    -webkit-user-drag: none;
  }
}

.lhc-backdrop {
  @apply absolute inset-0;

  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(4px);
  animation: lhc-fade-in 0.25s ease forwards;
}

.lhc-backdrop--out {
  animation: lhc-fade-in 0.24s ease reverse forwards;
}

@keyframes lhc-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.lhc-screen {
  @apply relative z-10 flex flex-col w-full;

  max-width: 480px;
  margin: 0 auto;
  padding: 20px 16px calc(env(safe-area-inset-bottom, 0px) + 20px);
  border-radius: 22px 22px 0 0;
  background: #010612;
  border-top: 1px solid #1d2329;
  overflow: clip;
}

.lhc-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.lhc-title {
  font-size: 15px;
  font-weight: 700;
  line-height: 15px;
  color: #fff;
}

.lhc-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #fff;

  :deep(svg) {
    width: 18px;
    height: 18px;
  }
}

.lhc-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.lhc-fechar {
  margin-top: 16px;
  width: 100%;
  min-height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  border: 1px solid #1d2329;
  border-radius: 10px;
  background: #010612;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  backdrop-filter: blur(9px);
}
</style>
